import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, Trash, UserCircle, Check, X, PencilSimple, Key, Eye, EyeSlash } from '@phosphor-icons/react';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuth } from '@/context/AuthContext';
import { LoadingOrEmpty } from '@/components/LoadingOrEmpty';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleBadge = (role) => {
  const map = {
    admin: { label: 'Administrador', color: '#C1121F', bg: '#C1121F20' },
    coordinador: { label: 'Coordinador', color: '#F5A623', bg: '#F5A62320' },
    solicitante: { label: 'Solicitante', color: '#495057', bg: '#F1F3F5' },
  };
  return map[role] || map.solicitante;
};

const UsuarioRow = ({ usuario, onToggleActivo, onDelete, onEdit, onResetPassword, isSelf }) => {
  const badge = roleBadge(usuario.role);
  return (
    <tr
      data-testid={`usuario-row-${usuario.id}`}
      className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA] transition-colors"
    >
      <td className="p-4">
        <p className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">{usuario.name}</p>
        <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans']">{usuario.email}</p>
      </td>
      <td className="p-4">
        <span
          className="px-2 py-1 text-xs font-bold font-['IBM_Plex_Sans']"
          style={{ backgroundColor: badge.bg, color: badge.color }}
        >
          {badge.label}
        </span>
      </td>
      <td className="p-4 text-sm font-['IBM_Plex_Sans'] text-[#495057]">
        {usuario.area || '—'}
      </td>
      <td className="p-4 text-sm font-['IBM_Plex_Sans'] text-[#495057]">
        {usuario.phone || '—'}
      </td>
      <td className="p-4">
        <span
          data-testid={`usuario-estado-${usuario.id}`}
          className="px-2 py-1 text-xs font-bold font-['IBM_Plex_Sans']"
          style={{
            backgroundColor: usuario.activo ? '#00863020' : '#6C757D20',
            color: usuario.activo ? '#008630' : '#6C757D',
          }}
        >
          {usuario.activo ? 'Activo' : 'Desactivado'}
        </span>
      </td>
      <td className="p-4 text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A] font-medium text-center">
        {usuario.movilizaciones_count}
      </td>
      <td className="p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid={`edit-usuario-${usuario.id}`}
            variant="outline"
            size="sm"
            onClick={() => onEdit(usuario)}
            className="gap-2 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
          >
            <PencilSimple size={14} weight="bold" /> Editar
          </Button>
          <Button
            data-testid={`reset-password-${usuario.id}`}
            variant="outline"
            size="sm"
            onClick={() => onResetPassword(usuario)}
            className="gap-2 border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623] hover:text-white font-['IBM_Plex_Sans']"
          >
            <Key size={14} weight="bold" /> Contraseña
          </Button>
          <Button
            data-testid={`toggle-activo-${usuario.id}`}
            variant="outline"
            size="sm"
            disabled={isSelf}
            onClick={() => onToggleActivo(usuario)}
            className="gap-2 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
          >
            {usuario.activo ? (
              <>
                <X size={14} weight="bold" /> Desactivar
              </>
            ) : (
              <>
                <Check size={14} weight="bold" /> Activar
              </>
            )}
          </Button>
          <Button
            data-testid={`delete-usuario-${usuario.id}`}
            variant="outline"
            size="sm"
            disabled={isSelf}
            onClick={() => onDelete(usuario)}
            className="gap-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-['IBM_Plex_Sans']"
          >
            <Trash size={14} weight="bold" /> Eliminar
          </Button>
        </div>
      </td>
    </tr>
  );
};

const DeleteUsuarioDialog = ({ usuario, open, onOpenChange, onConfirm, loading }) => {
  const [action, setAction] = useState('delete');
  const [reassignTo, setReassignTo] = useState('');
  const [candidatos, setCandidatos] = useState([]);

  useEffect(() => {
    if (!open || !usuario) return;
    setAction('delete');
    setReassignTo('');
    if (usuario.movilizaciones_count > 0 && usuario.role === 'solicitante') {
      axios
        .get(`${API_URL}/api/admin/usuarios/candidatos-reasignacion`, {
          params: { exclude: usuario.id },
          withCredentials: true,
        })
        .then((r) => setCandidatos(r.data))
        .catch(() => setCandidatos([]));
    } else {
      setCandidatos([]);
    }
  }, [open, usuario]);

  if (!usuario) return null;

  const submit = () => {
    if (action === 'reassign' && !reassignTo) {
      toast.error('Selecciona un solicitante destino para reasignar');
      return;
    }
    onConfirm({ movilizaciones_action: action, reassign_to: reassignTo || null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
            Eliminar Usuario
          </DialogTitle>
          <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
            {usuario.name} ({usuario.email})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {usuario.movilizaciones_count > 0 ? (
            <>
              <div className="p-3 bg-[#FFF7E6] border border-[#F5A623]">
                <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">
                  Este usuario tiene <strong>{usuario.movilizaciones_count}</strong> movilización(es).
                  ¿Qué hacer con ellas?
                </p>
              </div>

              <RadioGroup value={action} onValueChange={setAction} data-testid="delete-action-radio">
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="delete" id="opt-delete" data-testid="opt-delete" />
                  <Label htmlFor="opt-delete" className="font-['IBM_Plex_Sans'] text-sm cursor-pointer">
                    <strong>Eliminar</strong> también todas sus movilizaciones
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem
                    value="reassign"
                    id="opt-reassign"
                    data-testid="opt-reassign"
                    disabled={usuario.role !== 'solicitante' || candidatos.length === 0}
                  />
                  <Label htmlFor="opt-reassign" className="font-['IBM_Plex_Sans'] text-sm cursor-pointer">
                    <strong>Reasignar</strong> movilizaciones a otro solicitante activo
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="keep" id="opt-keep" data-testid="opt-keep" />
                  <Label htmlFor="opt-keep" className="font-['IBM_Plex_Sans'] text-sm cursor-pointer">
                    <strong>Conservar</strong> como registro histórico (marca como "[eliminado]")
                  </Label>
                </div>
              </RadioGroup>

              {action === 'reassign' && (
                <div className="space-y-2">
                  <Label className="font-['IBM_Plex_Sans'] text-sm">Solicitante destino</Label>
                  <Select value={reassignTo} onValueChange={setReassignTo}>
                    <SelectTrigger data-testid="reassign-target-select" className="border-[#E9ECEF]">
                      <SelectValue placeholder="Selecciona un solicitante" />
                    </SelectTrigger>
                    <SelectContent>
                      {candidatos.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} · {c.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          ) : (
            <div className="p-3 bg-[#F8F9FA] border border-[#E9ECEF]">
              <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">
                Este usuario no tiene movilizaciones. Se eliminará únicamente la cuenta.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-usuario-button"
              onClick={submit}
              disabled={loading}
              className="flex-1 bg-[#FF3B30] hover:bg-[#c22e26] text-white font-['IBM_Plex_Sans']"
            >
              {loading ? 'Eliminando...' : 'Eliminar Usuario'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EditUsuarioDialog = ({ usuario, open, onOpenChange, onConfirm, loading, currentUser }) => {
  const [form, setForm] = useState({ name: '', area: '', phone: '', role: 'solicitante' });

  useEffect(() => {
    if (open && usuario) {
      setForm({
        name: usuario.name || '',
        area: usuario.area || '',
        phone: usuario.phone || '',
        role: usuario.role || 'solicitante',
      });
    }
  }, [open, usuario]);

  if (!usuario) return null;

  const isSelf = currentUser && currentUser.id === usuario.id;

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    onConfirm({
      name: form.name.trim(),
      area: form.area.trim(),
      phone: form.phone.trim(),
      role: form.role,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
            Editar Usuario
          </DialogTitle>
          <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
            {usuario.email}
          </DialogDescription>
        </DialogHeader>

        <form data-testid="edit-usuario-form" onSubmit={submit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Nombre completo *
            </Label>
            <Input
              id="edit-name"
              data-testid="edit-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-area" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Área
            </Label>
            <Input
              id="edit-area"
              data-testid="edit-area-input"
              value={form.area}
              onChange={(e) => setForm({ ...form, area: e.target.value })}
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-phone" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Teléfono
            </Label>
            <Input
              id="edit-phone"
              data-testid="edit-phone-input"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+593987654321"
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-role" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Rol
            </Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
              disabled={isSelf}
            >
              <SelectTrigger id="edit-role" data-testid="edit-role-select" className="border-[#E9ECEF] font-['IBM_Plex_Sans']">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="solicitante" data-testid="edit-role-option-solicitante">Solicitante</SelectItem>
                <SelectItem value="coordinador" data-testid="edit-role-option-coordinador">Coordinador</SelectItem>
                <SelectItem value="admin" data-testid="edit-role-option-admin">Administrador</SelectItem>
              </SelectContent>
            </Select>
            {isSelf && (
              <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans']">
                No puedes cambiar tu propio rol.
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="confirm-edit-usuario-button"
              disabled={loading}
              className="flex-1 bg-[#C1121F] hover:bg-[#8B0000] text-white font-['IBM_Plex_Sans']"
            >
              {loading ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ResetPasswordDialog = ({ usuario, open, onOpenChange, onConfirm, loading }) => {
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (open) {
      setPwd('');
      setPwd2('');
      setShow(false);
    }
  }, [open]);

  if (!usuario) return null;

  const submit = (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (pwd !== pwd2) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    onConfirm(pwd);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
            Restablecer contraseña
          </DialogTitle>
          <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
            {usuario.name} ({usuario.email})
          </DialogDescription>
        </DialogHeader>

        <form data-testid="reset-password-form" onSubmit={submit} className="space-y-4 mt-4">
          <div className="p-3 bg-[#FFF7E6] border border-[#F5A623]">
            <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">
              La nueva contraseña reemplazará inmediatamente la anterior. Compártela con el usuario
              de forma segura; no se enviará por correo.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pwd" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Nueva contraseña *
            </Label>
            <div className="relative">
              <Input
                id="new-pwd"
                data-testid="new-password-input"
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                minLength={8}
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans'] pr-10"
                required
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShow(!show)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6C757D] hover:text-[#0A0A0A]"
                tabIndex={-1}
              >
                {show ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pwd2" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Confirmar contraseña *
            </Label>
            <Input
              id="new-pwd2"
              data-testid="confirm-password-input"
              type={show ? 'text' : 'password'}
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              minLength={8}
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
              required
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="confirm-reset-password-button"
              disabled={loading}
              className="flex-1 bg-[#F5A623] hover:bg-[#e09515] text-white font-['IBM_Plex_Sans']"
            >
              {loading ? 'Guardando...' : 'Restablecer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const UsuariosPage = () => {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('todos');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [pwdLoading, setPwdLoading] = useState(false);

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter !== 'todos') params.role = roleFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await axios.get(`${API_URL}/api/admin/usuarios`, {
        params,
        withCredentials: true,
      });
      setUsuarios(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudieron cargar los usuarios');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, search]);

  useEffect(() => {
    const t = setTimeout(fetchUsuarios, 300);
    return () => clearTimeout(t);
  }, [fetchUsuarios]);

  const handleToggleActivo = async (u) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/usuarios/${u.id}/activo`,
        { activo: !u.activo },
        { withCredentials: true }
      );
      toast.success(`Usuario ${!u.activo ? 'activado' : 'desactivado'}`);
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDeleteConfirm = async (payload) => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API_URL}/api/admin/usuarios/${deleteTarget.id}`, {
        data: payload,
        withCredentials: true,
      });
      toast.success('Usuario eliminado');
      setDeleteTarget(null);
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditConfirm = async (payload) => {
    setEditLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/usuarios/${editTarget.id}`,
        payload,
        { withCredentials: true }
      );
      toast.success('Usuario actualizado');
      setEditTarget(null);
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    } finally {
      setEditLoading(false);
    }
  };

  const handleResetPasswordConfirm = async (newPassword) => {
    setPwdLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/usuarios/${pwdTarget.id}/password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      toast.success('Contraseña restablecida');
      setPwdTarget(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map((d) => d.msg || JSON.stringify(d)).join(' '));
      } else {
        toast.error(detail || 'Error al restablecer contraseña');
      }
    } finally {
      setPwdLoading(false);
    }
  };

  const stats = useMemo(() => {
    const total = usuarios.length;
    const activos = usuarios.filter((u) => u.activo).length;
    const solicitantes = usuarios.filter((u) => u.role === 'solicitante').length;
    return { total, activos, solicitantes };
  }, [usuarios]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <UserCircle size={32} weight="bold" className="text-[#C1121F]" />
          <div>
            <h1 className="text-3xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
              Gestión de Usuarios
            </h1>
            <p className="text-sm text-[#495057] font-['IBM_Plex_Sans']">
              Administra solicitantes, coordinadores y administradores.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-[#E9ECEF] p-4">
            <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] uppercase tracking-wide">Total</p>
            <p data-testid="stat-total" className="text-2xl font-black font-['Chivo'] text-[#0A0A0A]">{stats.total}</p>
          </div>
          <div className="bg-white border border-[#E9ECEF] p-4">
            <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] uppercase tracking-wide">Activos</p>
            <p data-testid="stat-activos" className="text-2xl font-black font-['Chivo'] text-[#008630]">{stats.activos}</p>
          </div>
          <div className="bg-white border border-[#E9ECEF] p-4">
            <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] uppercase tracking-wide">Solicitantes</p>
            <p data-testid="stat-solicitantes" className="text-2xl font-black font-['Chivo'] text-[#0A0A0A]">{stats.solicitantes}</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
            <Input
              data-testid="search-usuarios-input"
              placeholder="Buscar por nombre o correo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="role-filter-select" className="md:w-52 border-[#E9ECEF] font-['IBM_Plex_Sans']">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los roles</SelectItem>
              <SelectItem value="solicitante">Solicitantes</SelectItem>
              <SelectItem value="coordinador">Coordinadores</SelectItem>
              <SelectItem value="admin">Administradores</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-[#E9ECEF] overflow-x-auto">
          <table className="w-full" data-testid="usuarios-table">
            <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF]">
              <tr>
                {['Usuario', 'Rol', 'Área', 'Teléfono', 'Estado', 'Movs.', 'Acciones'].map((h) => (
                  <th
                    key={h}
                    className="p-4 text-left text-xs uppercase tracking-wide font-bold font-['IBM_Plex_Sans'] text-[#6C757D]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8">
                    <LoadingOrEmpty loading emptyMessage="" />
                  </td>
                </tr>
              ) : usuarios.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-[#6C757D] font-['IBM_Plex_Sans']">
                    No se encontraron usuarios con los filtros actuales.
                  </td>
                </tr>
              ) : (
                usuarios.map((u) => (
                  <UsuarioRow
                    key={u.id}
                    usuario={u}
                    isSelf={u.id === user?.id}
                    onToggleActivo={handleToggleActivo}
                    onDelete={setDeleteTarget}
                    onEdit={setEditTarget}
                    onResetPassword={setPwdTarget}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteUsuarioDialog
        usuario={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />

      <EditUsuarioDialog
        usuario={editTarget}
        open={!!editTarget}
        onOpenChange={(v) => !v && setEditTarget(null)}
        onConfirm={handleEditConfirm}
        loading={editLoading}
        currentUser={user}
      />

      <ResetPasswordDialog
        usuario={pwdTarget}
        open={!!pwdTarget}
        onOpenChange={(v) => !v && setPwdTarget(null)}
        onConfirm={handleResetPasswordConfirm}
        loading={pwdLoading}
      />
    </div>
  );
};

export default UsuariosPage;
